// src/components/Branch/config/sections/VideosSection.jsx
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  FilmIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  CheckCircleIcon,
  ExternalLinkIcon,
} from "@heroicons/react/outline";
import {
  addStoreVideoThunk,
  updateStoreVideoThunk,
  deleteStoreVideoThunk,
} from "../../../../store/thunks/branchDisplayThunks";
import Dropdown from "../../../common/Dropdown";

// Must match backend enum exactly
const VIDEO_TYPES = [
  { _id: "STORE_TOUR", name: "Store Tour" },
  { _id: "MAKING_PROCESS", name: "Making Process" },
  { _id: "TESTIMONIAL", name: "Testimonial" },
  { _id: "COLLECTION", name: "Collection" },
  { _id: "PROMO", name: "Promo" },
  { _id: "OTHER", name: "Other" },
];

const INITIAL_FORM = {
  type: "STORE_TOUR",
  title: "",
  url: "",
  thumbnailUrl: "",
  duration: "",
  isActive: true,
};

const VideosSection = ({ branchCode, isRootAdmin }) => {
  const dispatch = useDispatch();
  const { videos, loadingSection } = useSelector(
    (state) => state.branchDisplay
  );

  const [isAdding, setIsAdding] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [formData, setFormData] = useState(INITIAL_FORM);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleAdd = async () => {
    await dispatch(addStoreVideoThunk({ branchCode, videoData: formData }));
    handleCancel();
  };

  const handleEdit = (video, index) => {
    setEditingIndex(index);
    setFormData({
      type: video.type || "STORE_TOUR",
      title: video.title || "",
      url: video.url || "",
      thumbnailUrl: video.thumbnailUrl || "",
      duration: video.duration || "",
      isActive: video.isActive ?? true,
    });
  };

  const handleUpdate = async () => {
    await dispatch(
      updateStoreVideoThunk({
        branchCode,
        videoIndex: editingIndex,
        videoData: formData,
      })
    );
    handleCancel();
  };

  const handleDelete = async (index) => {
    if (window.confirm("Are you sure you want to delete this video?")) {
      await dispatch(deleteStoreVideoThunk({ branchCode, videoIndex: index }));
    }
  };

  const handleCancel = () => {
    setIsAdding(false);
    setEditingIndex(null);
    setFormData(INITIAL_FORM);
  };

  const renderForm = () => (
    <div className="bg-white border rounded-lg p-4 space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Dropdown
          label="Video Type"
          options={VIDEO_TYPES}
          selected={VIDEO_TYPES.find((t) => t._id === formData.type)}
          onChange={(selected) =>
            handleInputChange("type", selected?._id || "STORE_TOUR")
          }
        />
        <div>
          <label className="block text-sm text-gray-600 mb-1">Title *</label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => handleInputChange("title", e.target.value)}
            className="w-full px-3 py-2 border rounded-lg"
            placeholder="Video title"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm text-gray-600 mb-1">Video URL *</label>
        <input
          type="url"
          value={formData.url}
          onChange={(e) => handleInputChange("url", e.target.value)}
          className="w-full px-3 py-2 border rounded-lg"
          placeholder="https://youtube.com/watch?v=..."
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm text-gray-600 mb-1">
            Thumbnail URL
          </label>
          <input
            type="url"
            value={formData.thumbnailUrl}
            onChange={(e) => handleInputChange("thumbnailUrl", e.target.value)}
            className="w-full px-3 py-2 border rounded-lg"
            placeholder="https://..."
          />
        </div>
        <div>
          <label className="block text-sm text-gray-600 mb-1">Duration</label>
          <input
            type="text"
            value={formData.duration}
            onChange={(e) => handleInputChange("duration", e.target.value)}
            className="w-full px-3 py-2 border rounded-lg"
            placeholder="2:30"
          />
        </div>
      </div>

      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={formData.isActive}
          onChange={(e) => handleInputChange("isActive", e.target.checked)}
          className="h-4 w-4 rounded"
        />
        <span className="text-sm">Active</span>
      </label>

      <div className="flex gap-2 pt-2">
        <button
          onClick={editingIndex !== null ? handleUpdate : handleAdd}
          disabled={
            loadingSection === "videos" || !formData.title || !formData.url
          }
          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 flex items-center gap-2"
        >
          <CheckCircleIcon className="h-4 w-4" />
          {editingIndex !== null ? "Update Video" : "Add Video"}
        </button>
        <button
          onClick={handleCancel}
          className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
        >
          Cancel
        </button>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {isRootAdmin && !isAdding && editingIndex === null && (
        <button
          onClick={() => setIsAdding(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <PlusIcon className="h-4 w-4" />
          Add Video
        </button>
      )}

      {(isAdding || editingIndex !== null) && renderForm()}

      <div className="bg-gray-50 p-4 rounded-lg">
        <h4 className="font-medium mb-4 flex items-center">
          <FilmIcon className="h-4 w-4 mr-2" />
          Store Videos ({videos.length})
        </h4>

        {videos.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <FilmIcon className="h-12 w-12 mx-auto mb-2 text-gray-300" />
            <p>No videos added yet</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {videos.map((video, index) => (
              <div
                key={index}
                className={`bg-white border rounded-lg overflow-hidden ${
                  !video.isActive ? "opacity-60" : ""
                }`}
              >
                <div className="relative aspect-video bg-gray-200">
                  {video.thumbnailUrl ? (
                    <img
                      src={video.thumbnailUrl}
                      alt={video.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <FilmIcon className="h-12 w-12 text-gray-400" />
                    </div>
                  )}
                  {video.duration && (
                    <span className="absolute bottom-2 right-2 px-2 py-0.5 bg-black bg-opacity-70 text-white text-xs rounded">
                      {video.duration}
                    </span>
                  )}
                </div>
                <div className="p-3">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h5 className="font-medium text-sm truncate">
                        {video.title}
                      </h5>
                      <p className="text-xs text-gray-500">
                        {VIDEO_TYPES.find((t) => t._id === video.type)?.name}
                      </p>
                    </div>
                    {isRootAdmin && editingIndex === null && (
                      <div className="flex gap-1 ml-2">
                        <a
                          href={video.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-1 text-gray-600 hover:bg-gray-100 rounded"
                        >
                          <ExternalLinkIcon className="h-4 w-4" />
                        </a>
                        <button
                          onClick={() => handleEdit(video, index)}
                          className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(index)}
                          className="p-1 text-red-600 hover:bg-red-50 rounded"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default VideosSection;
